import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { combineLatest, EMPTY, from, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  Authenticate,
  CatalogLoadRequested,
  CatalogsChanged,
  TripActionTypes,
  TripChanged,
  TripFirstLoadRequested,
  TripFlightStatusRequested,
  TripLoaded,
  TripLoadRequested,
  UpdatePaxSelectedForCheckin,
} from './trip.actions';
import { SegmentMilitaryPaxTravelType } from '~app/state/trip/segment/segment.actions';
import { AddAuthToken, AuthTokenActionTypes } from '../auth-token/auth-token.actions';
import {
  ErrorActionTypes,
  SetDelayedFlightNotification,
  SetReferToAgentDontPrintError,
  SetTimeoutError,
} from '../error/error.action';
import { FlightStatuses } from './flight/flight.model';
import { FlightStatus } from '../flight-status/flight-status.model';
import { FlightStatusLoaded, FlightStatusRequested } from '../flight-status/flight-status.actions';
import { TripService } from '../../services/api/trip/trip.service';
import { FlightService } from '../../services/api/flight/flight.service';
import { AuthenticationService } from '../../services/api/authentication/authentication.service';
import { SegmentDetailAndPax, Trip } from './trip.model';
import { AppState, AuthTokenState } from '../index';
import { selectCurrentTrip } from './trip.selectors';
import { errorHelpers } from '../error/error-helpers';
import { DeviceService } from '../../services/ha-cuss/device.service';
import { CatalogService } from '../../services/api/catalog/catalog.service';
import { ConfigService } from '../../services/api/config/config.service';
import { CardReaderResetCount } from '../ha-cuss-device/card-reader/card-reader.actions';
import { Logging } from '../../services/logging/logging.service';
import { locatedPnr } from '../../services/emitters/session-event-emitters';
import { CatalogIdTypes } from './passenger.catalog.model';
import { AppRoutes } from '~app/app-routes';
import { MILITARY_TRAVEL_TYPE } from '~app/models/military/military.model';
import { isEmpty } from 'shared/embross/helper';

@Injectable()
export class TripEffects {
  public enableInfantConfig;
  public enableInternationalConfig;
  public airportCodeConfig;
  public kioskConfig;

  public catalogs$ = new ReplaySubject(1);
  public catalogsUpdated$ = new Subject();

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private flightService: FlightService,
    private authService: AuthenticationService,
    private tripService: TripService,
    private deviceService: DeviceService,
    public catalogService: CatalogService,
    public configService: ConfigService,
    protected logging: Logging
  ) {
    this.configService.config.subscribe((x) => {
      if (x && x.configuration) {
        this.enableInfantConfig = x.configuration.enableInfants;
        this.enableInternationalConfig = x.configuration.enableInternational;
        this.airportCodeConfig = x.airportCode;
        this.kioskConfig = x.configuration;
      }
    });
  }

  @Effect({ dispatch: false })
  loadFirstTrip$ = this.actions$.pipe(
    ofType(TripActionTypes.TripFirstLoadRequested),
    withLatestFrom(this.store.select('authentication'), this.store.select('pnrLocatorFlow')),
    switchMap(([_, authToken, pnrLocatorFlow]) => {
      return from(this.tripService.getTripDataBased(authToken, pnrLocatorFlow)).pipe(
        map((tripResponse: any) => {
          if (!tripResponse || !tripResponse.results || tripResponse.results.length === 0) {
            return;
          }

          this.resetsHardwareAndDisableDevices();

          let trip = this.deserializeTrip(tripResponse);

          if (!trip) {
            return;
          }
          this.store.dispatch(new TripLoaded({ trip }));
          trip = this.validateTrip(trip);

          // We only want to dispatch these actions if we have a truthy trip
          if (trip) {
            this.store.dispatch(new TripFlightStatusRequested());
            this.store.dispatch(new CatalogLoadRequested());
          } else {
            this.logging.infoUiInvalidTrip();
          }

          return trip;
        })
      );
    })
  );

  @Effect({ dispatch: false })
  loadTrip$ = this.actions$.pipe(
    ofType(TripActionTypes.TripLoadRequested),
    withLatestFrom(
      this.store.select((store) => store.authentication),
      this.store.select((store) => store.pnrLocatorFlow)
    ),
    switchMap(([_, authToken, pnrLocatorFlow]) => {
      return from(this.tripService.getTripDataBased(authToken, pnrLocatorFlow)).pipe(
        map((tripResponse: any) => {
          if (!tripResponse || !tripResponse.results || tripResponse.results.length === 0) {
            return;
          }

          this.resetsHardwareAndDisableDevices();

          let trip = this.deserializeTrip(tripResponse);
          trip = this.validateTrip(trip);

          if (!trip) {
            this.logging.infoUiInvalidTrip();
            return;
          }

          this.store.dispatch(new TripLoaded({ trip }));
          if (trip) {
            this.store.dispatch(new CatalogLoadRequested());
          }

          return trip;
        })
      );
    })
  );

  @Effect({ dispatch: false })
  selectNextSegment$ = this.actions$.pipe(
    ofType(TripActionTypes.NextSegmentSelected),
    withLatestFrom(this.store.select(selectCurrentTrip)),
    map(([_, trip]) => {
      const nextSegment = trip.getNextSegmentToCheckIn();
      const isFirstSegment = false;

      if (!nextSegment) {
        return false;
      }

      const newTrip = trip.clone();
      newTrip.activeSegment = newTrip.segments.find((segment) => segment.id === nextSegment.id);
      newTrip.activeSegment.nextFlight = newTrip.activeSegment.flights[0];

      this.store.dispatch(new TripChanged({ newTrip }));
      if (!this.dispatchTripErrorActions(newTrip, isFirstSegment)) {
        this.store.dispatch(new TripFlightStatusRequested());
        this.store.dispatch(new CatalogLoadRequested());
      }
      return newTrip;
    })
  );

  @Effect({ dispatch: false })
  selectCurrentAirportSegment$ = this.actions$.pipe(
    ofType(TripActionTypes.DisplayFlightInfoForKioskOrigin),
    withLatestFrom(this.store.select(selectCurrentTrip)),
    map(([_, trip]) => {
      const newTrip = trip;
      const currentLocationTrip = trip.findActiveSegment();

      newTrip.activeSegment = newTrip.findActiveSegment();
      const flightId = currentLocationTrip.flights[0].id;

      this.store.dispatch(new TripChanged({ newTrip }));
      this.store.dispatch(new FlightStatusRequested({ flightId }));

      return currentLocationTrip;
    })
  );

  @Effect({ dispatch: false })
  authenticate$ = this.actions$.pipe(
    ofType(TripActionTypes.Authenticate),
    map((action: Authenticate) => action.payload),
    switchMap((code) => from(this.authService.authenticate(code))),
    map((response: AuthTokenState) => {
      if (response && response.access_token) {
        this.store.dispatch(new AddAuthToken(response));
      }

      return response;
    }),
    catchError(() => EMPTY)
  );

  @Effect({ dispatch: false })
  getTrip$ = this.actions$.pipe(
    ofType(AuthTokenActionTypes.AddAuthToken),
    map(() => {
      this.store.dispatch(new TripFirstLoadRequested());
    })
  );

  @Effect({ dispatch: false })
  validateFlight$ = this.actions$.pipe(
    ofType(TripActionTypes.TripFlightStatusRequested),
    withLatestFrom(this.store.select(selectCurrentTrip)),
    map(([_, trip]) => {
      this.getFlightStatusAndCheckDelayed(trip.activeSegment.nextFlight.id).subscribe((flightStatusResult) => {
        let route: string;
        if (trip.activeSegment.hasCheckedInPax() && (this.kioskConfig.enableSeats || this.kioskConfig.enableBags)) {
          route = AppRoutes.OPTIONS;
        } else {
          route = AppRoutes.ITINERARY;
        }

        if (flightStatusResult) {
          const { flightStatus, flightSubStatus, boardingStatus } = flightStatusResult;
          const boardingStartTime = flightStatusResult.boardingStartTime.airportDateTimeString;
          const flightLoggingData = {
            flightStatus,
            flightSubStatus,
            boardingStatus,
            boardingStartTime,
          };
          this.logging.infoUiCheckinStarted(flightLoggingData);
          if (flightStatusResult.flightStatus === FlightStatuses.Delayed) {
            this.store.dispatch(
              new SetDelayedFlightNotification({
                departureDate: new Date(flightStatusResult.departure.airportDateTimeString),
                okRoute: route,
              })
            );
          } else {
            this.router.navigate([route]);
          }
        } else {
          this.store.dispatch(new SetReferToAgentDontPrintError({ alertReasonCode: '' }));
        }
      });
    })
  );

  @Effect({ dispatch: false })
  getCatalog$ = this.actions$.pipe(
    ofType(TripActionTypes.CatalogLoadRequested),
    withLatestFrom(this.store.pipe(select(selectCurrentTrip))),
    switchMap(([_, trip]) => {
      return combineLatest([
        of(trip),
        from(this.catalogService.getCatalogs(trip.confirmationCode, trip.activeSegment.id, trip.id)),
      ]);
    }),
    map(([trip, catalogs]) => {
      const newTrip = trip.clone();
      let seatsCatalogs = [];
      let bagsCatalogs = [];

      this.catalogs$.next(catalogs);
      this.catalogs$.complete();

      if (catalogs.results && catalogs.results.length > 0) {
        seatsCatalogs = catalogs.results.filter((catalog) => catalog.id === CatalogIdTypes.Seats)[0].entries;
        bagsCatalogs = catalogs.results.filter((catalog) => catalog.id === CatalogIdTypes.Bags)[0].entries;
        this.catalogsUpdated$.next(null);
      }

      bagsCatalogs.forEach((catalog) => {
        const productsMissingMaxQuantity = catalog.products.filter(
          (product) => product.maxQuantity === null || product.maxQuantity === undefined
        );
        if (productsMissingMaxQuantity && productsMissingMaxQuantity.length > 0) {
          this.logging.infoApiBagItemMissingMaxQuantity(productsMissingMaxQuantity);
        }

        const productsMaxQuantityZero = catalog.products.find((product) => product.maxQuantity === 0);
        if (productsMaxQuantityZero && productsMaxQuantityZero.length > 0) {
          const productsZeroMaxQuantity = catalog.products.find((product) => product.maxQuantity === 0);
          this.logging.infoApiBagItemMaxQuantityZero(productsZeroMaxQuantity);
        }
      });

      this.store.dispatch(new CatalogsChanged({ seatsCatalogs, bagsCatalogs }));

      return newTrip;
    })
  );

  @Effect({ dispatch: false })
  updateTrip$ = this.actions$.pipe(
    ofType(TripActionTypes.UpdateTripDataRequested),
    withLatestFrom(
      this.store.select((store) => store.authentication),
      this.store.select((store) => store.pnrLocatorFlow),
      this.store.select(selectCurrentTrip)
    ),
    switchMap(([_, authToken, pnrLocatorFlow, currentTrip]) => {
      return from(this.tripService.getTripDataBased(authToken, pnrLocatorFlow)).pipe(
        map((tripResponse: any) => {
          if (!tripResponse || !tripResponse.results || tripResponse.results.length === 0) {
            return;
          }
          const trip = this.deserializeTrip(tripResponse);

          if (!trip) {
            return;
          }

          if (currentTrip.activeSegment) {
            trip.activeSegment = trip.segments.find((segment) => segment.id === currentTrip.activeSegment.id);
          }

          this.tripService.setCartLineItemInfo(trip.activeSegment.details);

          this.store.dispatch(new TripLoaded({ trip }));

          // Before we get the new trip, we need to capture the old trip's pax that are selected
          this.store.dispatch(
            new UpdatePaxSelectedForCheckin({ paxIds: currentTrip.activeSegment.getPaxIdsSelectedForCheckin() })
          );

          return trip;
        })
      );
    })
  );

  @Effect({ dispatch: false })
  updateMilitaryTravelType$ = this.actions$.pipe(
    ofType(TripActionTypes.SetMilitaryTravelTypeRequested),
    withLatestFrom(this.store.select(selectCurrentTrip)),
    switchMap(([_, trip]) => {
      const paxAndSegmentDetail = trip.getSegmentDetailsAndPaxSelected();
      const militaryPax: SegmentDetailAndPax[] = [];

      paxAndSegmentDetail.forEach((segmentDetailAndPax: SegmentDetailAndPax) => {
        if (segmentDetailAndPax.segmentDetail.flightDetails[0].militaryTravelType !== MILITARY_TRAVEL_TYPE.NONE) {
          militaryPax.push(segmentDetailAndPax);
        }
      });

      if (isEmpty(militaryPax)) {
        this.router.navigate([AppRoutes.BAGS]);
        return;
      }

      return from(this.tripService.updateMilitaryTravelType(trip.id, trip.activeSegment.id, militaryPax));
    }),
    filter((result) => !(result instanceof SetTimeoutError)),
    map(() => {
      this.catalogsUpdated$.pipe(take(1)).subscribe(() => {
        this.logging.infoApiMilitaryInfoMatchSuccessful(0);
      });
      this.store.dispatch(new TripLoadRequested());
      this.router.navigate([AppRoutes.MILITARY_THANK_YOU]);
    }),
    catchError(() => of(this.router.navigate([AppRoutes.MILITARY_NO_MATCH])))
  );

  @Effect({ dispatch: false })
  militaryNoMatch$ = this.actions$.pipe(
    ofType(ErrorActionTypes.SetMilitaryNoMatchError),
    withLatestFrom(this.store.select(selectCurrentTrip)),
    map(([_, trip]) => {
      const allPaxTravelTypeNone = trip.activeSegment.details.map((segmentDetail) => {
        return {
          selectedMilitaryTravelType: MILITARY_TRAVEL_TYPE.NONE,
          segmentFlight: segmentDetail.flightDetails[0],
        };
      });

      this.store.dispatch(new SegmentMilitaryPaxTravelType(allPaxTravelTypeNone));
      this.router.navigate([AppRoutes.MILITARY_NO_MATCH]);
    })
  );

  private deserializeTrip(tripResponse) {
    const tripJson = tripResponse.results[0];

    tripJson.flights = tripJson.flights && tripJson.flights.entries ? tripJson.flights.entries : [];
    tripJson.segments = tripJson.segments && tripJson.segments.entries ? tripJson.segments.entries : [];
    tripJson.passengers = tripJson.passengers && tripJson.passengers.entries ? tripJson.passengers.entries : [];

    return Trip.deserializeFromJson(tripJson, this.airportCodeConfig);
  }

  private resetsHardwareAndDisableDevices() {
    /*At this point we know that we have an active trip and can disable all the methods allowed to lookup with the hardware devices*/
    this.store.dispatch(new CardReaderResetCount());
    locatedPnr.next(true);
    this.deviceService.disableLookupDevices();
  }

  private validateTrip(trip) {
    const isFirstSegment = true;

    if (this.dispatchTripErrorActions(trip, isFirstSegment)) {
      return false;
    }

    const isOnMidPointJourneyNotCheckedIn =
      trip.activeSegment.isOnMidPointJourney() && !trip.activeSegment.areAllPassengersCheckedIn;
    /*
     * The API does not allow printing boarding passes if the segment is not checked in,
     * So if the passenger is on midpoint journey and is not checked in the application will stop him from going any further
     * */
    if (isOnMidPointJourneyNotCheckedIn) {
      this.store.dispatch(new SetReferToAgentDontPrintError({ alertReasonCode: '' }));
      return;
    }

    return trip;
  }

  dispatchTripErrorActions(trip: Trip, isFirstSegment: boolean): boolean {
    if (!trip.activeSegment) {
      this.store.dispatch(new SetReferToAgentDontPrintError({ alertReasonCode: '' }));
      return true;
    }

    if (
      !trip.activeSegment.isOnMidPointJourney() &&
      trip.activeSegment.origin !== this.airportCodeConfig &&
      isFirstSegment
    ) {
      this.store.dispatch(new SetReferToAgentDontPrintError({ alertReasonCode: '' }));
    }

    if (trip.activeSegment.details[0].alertMessageCode) {
      errorHelpers.setSegmentRelatedError(trip.activeSegment, this.store);
      return true;
    }

    /*
     * For now the API does not support infants and international trips so we're displaying
     * a generic error message for those cases
     */
    let checkError = false;
    if (trip.activeSegment.isInternationalMarketType() && !this.enableInternationalConfig) {
      checkError = true;
    }

    if (trip.hasInfant() && !this.enableInfantConfig) {
      checkError = true;
    }

    if (checkError) {
      this.store.dispatch(new SetReferToAgentDontPrintError({ alertReasonCode: '' }));
      return true;
    }

    return false;
  }

  public getFlightStatusAndCheckDelayed(flightId: string): Observable<FlightStatus | any> {
    const promise = this.flightService.getFlightStatusDataBasedId(flightId);

    return from(promise).pipe(
      map((response: any) => {
        let flightResult = null;

        if (response && response.results && response.results.length > 0) {
          flightResult = response.results[0];

          const flightStatus = FlightStatus.deserializeFromJson(flightResult);
          this.store.dispatch(new FlightStatusLoaded({ flightStatus }));
          return flightStatus;
        } else {
          return null;
        }
      })
    );
  }
}
